import { Component, computed, HostListener, inject, input, output, signal, viewChild } from '@angular/core';
import { RidAudioButton } from '../../components/rid-audio-button/rid-audio-button';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { PronunciationService } from '../../services/pronunciation.service';
import { AudioEffectPlayer } from '../../utils/audio.utils';
import { calculateLevenshteinDistance } from '../../utils/math.utils';

const ALLOWED_CHAR = /^[a-zA-Z.,!?'-]$/;

function normalizeForComparison(text: string) {
  return text.toLowerCase().replace(/[.,!?'-]/g, '');
}

@Component({
  selector: 'rid-dictation-digital-sentences',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidAudioButton,
    RidBottomButton,
    RidTextInput,
    RidSfxDirective,
  ],
  templateUrl: './rid-dictation-digital-sentences.html',
  host: {
    class: 'block',
  },
})
export class RidDictationDigitalSentences {
  private readonly $pronunciationService = inject(PronunciationService);

  sentences = input.required<string[]>();
  errorMargin = input<number>(0.5);
  maxCloseAttempts = input<number>(2);

  index = signal(0);
  sentence = computed(() => this.sentences()[this.index()]);
  sentenceKey = computed(() => this.sentence());

  state = signal<{ type: 'AUDIO' } | { type: 'WRITING' } | { type: 'FEEDBACK' } | { type: 'RETRY' } | { type: 'REVEAL' }>({
    type: 'AUDIO',
  });
  text = signal<string[]>([]);
  mismatchIndices = signal<ReadonlySet<number>>(new Set());
  appearance = signal<'DEFAULT' | 'CORRECT' | 'INCORRECT' | 'ALMOST'>('DEFAULT');

  displayChars = computed(() => {
    const typed = this.text();
    if (this.appearance() !== 'ALMOST') return typed;

    const correctSentence = this.sentence();
    return Array.from({ length: Math.max(typed.length, correctSentence.length) }, (_, i) => typed[i] ?? correctSentence[i]);
  });

  audioButton = viewChild.required<RidAudioButton>('audioButton');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');

  instruction = signal('Luister goed naar de zin');
  closeAttempts = signal(0);

  finished = output<void>();

  async start() {
    if (this.state().type !== 'AUDIO') return;

    await this.audioButton().play();

    if (this.state().type === 'AUDIO') {
      this.instruction.set('Typ de zin die je hoort');
      this.state.set({ type: 'WRITING' });
    }
  }

  async check() {
    const state = this.state();
    if (state.type !== 'WRITING') return;

    const text = this.text().join('');
    const correctSentence = this.sentence();
    this.state.set({ type: 'FEEDBACK' });

    if (text === correctSentence) {
      this.mismatchIndices.set(new Set());
      await this.showCorrect();
      this.next();
      return;
    }

    const mismatches = new Set<number>();
    const maxLength = Math.max(this.text().length, correctSentence.length);
    for (let i = 0; i < maxLength; i++) {
      if (this.text()[i] !== correctSentence[i]) mismatches.add(i);
    }
    this.mismatchIndices.set(mismatches);

    if (normalizeForComparison(text) === normalizeForComparison(correctSentence)) {
      this.instruction.set('Bijna goed! Let op hoofdletters en leestekens');
      await this.showAlmostCorrect();
      this.next();
      return;
    }

    const distance = calculateLevenshteinDistance(text.split(''), correctSentence.split(''));
    const error = distance / text.length;
    const isCloseAttempt = error < this.errorMargin();
    if (isCloseAttempt) this.closeAttempts.update((c) => c + 1);
    const doReveal = this.closeAttempts() >= this.maxCloseAttempts();

    if (!doReveal) {
      if (isCloseAttempt) this.instruction.set('Bijna! Probeer het nog een keer');
      else this.instruction.set('Probeer het nog eens');
    } else {
      this.instruction.set('Het goede antwoord was...');
    }
    await this.showIncorrect();

    if (doReveal) {
      this.reveal();
    } else {
      this.state.set({ type: 'RETRY' });
    }
  }

  reveal() {
    this.state.set({ type: 'REVEAL' });
  }

  async showCorrect() {
    this.appearance.set('CORRECT');
    await Promise.all([
      this.overlayFeedback().showCorrect(),
      AudioEffectPlayer.play('CORRECT'),
      this.$pronunciationService.tryPronounce(this.sentence()),
    ]);
    this.appearance.set('DEFAULT');
  }

  async showAlmostCorrect() {
    this.appearance.set('ALMOST');
    await Promise.all([
      this.overlayFeedback().showAlmost(),
      AudioEffectPlayer.play('WARNING'),
      this.$pronunciationService.tryPronounce(this.sentence()),
    ]);
    this.appearance.set('DEFAULT');
  }

  async showIncorrect() {
    this.appearance.set('INCORRECT');
    await Promise.all([
      this.overlayFeedback().showIncorrect(), //
      AudioEffectPlayer.play('INCORRECT'),
    ]);
  }

  retry() {
    this.appearance.set('DEFAULT');
    this.state.set({ type: 'WRITING' });
    this.text.set([]);
    this.mismatchIndices.set(new Set());
    this.instruction.set('Typ de zin die je hoort');
  }

  next() {
    const nextIndex = this.index() + 1;
    if (nextIndex >= this.sentences().length) {
      this.finished.emit();
      return;
    }

    this.index.set(nextIndex);
    this.text.set([]);
    this.mismatchIndices.set(new Set());
    this.closeAttempts.set(0);
    this.instruction.set('Luister goed naar de zin');
    this.state.set({ type: 'AUDIO' });
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const stateType = this.state().type;
    if (stateType !== 'WRITING' && stateType !== 'AUDIO') return;

    if (event.key === ' ') {
      event.preventDefault();
      this.append(' ');
      return;
    }

    if (event.key.length === 1 && ALLOWED_CHAR.test(event.key)) {
      this.append(event.key);
      return;
    }

    if (event.key === 'Backspace') {
      this.pop();
    }
  }

  append(char: string) {
    this.text.set([...this.text(), char]);
  }

  pop() {
    this.text.set(this.text().slice(0, -1));
  }
}
